import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { formatCurrency, formatDateTime } from "@nomnom/core";
import { getStatusColor, getStatusIcon } from "../utils/dashboardUtils";
import type { Order, OrderStatus, User } from "../types/firestore";
import {
  buildOrderRows,
  buildUsersById,
  cancelOrderById,
  filterOrderRows,
  subscribeToOrderDiningHalls,
  subscribeToOrders,
  subscribeToUsers,
  type OrderRow,
} from "../services/ordersService";

const STATUS_OPTIONS: Array<{ value: "all" | OrderStatus; label: string }> = [
  { value: "all", label: "All Status" },
  { value: "searching", label: "Searching" },
  { value: "assigned", label: "Assigned" },
  { value: "at_pickup", label: "At Pickup" },
  { value: "picked_up", label: "Picked Up" },
  { value: "arriving", label: "Arriving" },
  { value: "delivered", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
];

export default function Orders() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [diningHalls, setDiningHalls] = useState<Record<string, string>>({});

  const [ordersReady, setOrdersReady] = useState(false);
  const [usersReady, setUsersReady] = useState(false);
  const [hallsReady, setHallsReady] = useState(false);

  const [selectedOrder, setSelectedOrder] = useState<OrderRow | null>(null);
  const [actionDialog, setActionDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const [filter, setFilter] = useState<"all" | OrderStatus>("all");
  const [search, setSearch] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    return subscribeToOrders(
      (nextOrders) => {
        setOrders(nextOrders);
        setOrdersReady(true);
        setError(null);
      },
      (message) => {
        setOrdersReady(true);
        setError(message);
      },
    );
  }, []);

  useEffect(() => {
    return subscribeToUsers(
      (nextUsers) => {
        setUsers(nextUsers);
        setUsersReady(true);
      },
      (message) => {
        setUsersReady(true);
        setError(message);
      },
    );
  }, []);

  useEffect(() => {
    return subscribeToOrderDiningHalls(
      (nextDiningHalls) => {
        setDiningHalls(nextDiningHalls);
        setHallsReady(true);
      },
      (message) => {
        setHallsReady(true);
        setError(message);
      },
    );
  }, []);

  const loading = !(ordersReady && usersReady && hallsReady);

  const usersById = useMemo(() => buildUsersById(users), [users]);

  const orderRows = useMemo(
    () => buildOrderRows(orders, usersById, diningHalls),
    [diningHalls, orders, usersById],
  );

  const filteredOrders = useMemo(
    () => filterOrderRows(orderRows, filter, search),
    [filter, orderRows, search],
  );

  const openCancelDialog = (order: OrderRow) => {
    setSelectedOrder(order);
    setCancelReason("");
    setActionDialog(true);
    setSuccess(null);
    setError(null);
  };

  const closeDialog = () => {
    if (actionLoading) {
      return;
    }

    setActionDialog(false);
    setSelectedOrder(null);
    setCancelReason("");
  };

  const cancelOrder = async () => {
    if (!selectedOrder) {
      return;
    }

    setActionLoading(true);
    setSuccess(null);
    setError(null);

    try {
      await cancelOrderById(selectedOrder.id, cancelReason);
      setSuccess(`Order #${selectedOrder.id} was cancelled.`);
      setActionDialog(false);
      setSelectedOrder(null);
      setCancelReason("");
    } catch (updateError) {
      setError(
        updateError instanceof Error
          ? updateError.message
          : "Failed to cancel the order.",
      );
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Orders</Typography>
        <Typography variant="body2" color="text.secondary">
          Live updates enabled
        </Typography>
      </Box>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {success && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="success">{success}</Alert>
        </Box>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <TextField
            placeholder="Search orders..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} />,
            }}
            sx={{ flex: 1 }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filter}
              label="Status"
              onChange={(event) => setFilter(event.target.value as "all" | OrderStatus)}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order ID</TableCell>
              <TableCell>Student</TableCell>
              <TableCell>Rider</TableCell>
              <TableCell>Dining Hall</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : filteredOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography color="text.secondary" align="center" py={4}>
                    No orders found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredOrders.map((order) => {
                const createdAtLabel = formatDateTime(order.createdAt, "Unknown");
                const isCancellable =
                  order.status !== "cancelled" && order.status !== "delivered";

                return (
                  <TableRow key={order.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        #{order.id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{order.studentName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {order.studentEmail}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{order.riderName}</TableCell>
                    <TableCell>{order.diningHall}</TableCell>
                    <TableCell>
                      <Chip
                        label={order.status.replace("_", " ")}
                        color={getStatusColor(order.status)}
                        size="small"
                        icon={getStatusIcon(order.status)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatCurrency(order.amount)}
                        {order.tip > 0 && ` (tip ${formatCurrency(order.tip)})`}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{createdAtLabel}</Typography>
                    </TableCell>
                    <TableCell>
                      {isCancellable ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => openCancelDialog(order)}
                        >
                          Cancel
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No actions
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={actionDialog} onClose={closeDialog}>
        <DialogTitle>Cancel Order</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Order #{selectedOrder?.id} - {selectedOrder?.studentName}
          </Typography>
          <TextField
            label="Cancellation Reason"
            multiline
            rows={3}
            placeholder="Optional note for support context"
            fullWidth
            sx={{ mt: 2 }}
            value={cancelReason}
            onChange={(event) => setCancelReason(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={actionLoading}>
            Close
          </Button>
          <Button
            onClick={cancelOrder}
            variant="contained"
            color="error"
            disabled={actionLoading}
          >
            {actionLoading ? "Cancelling..." : "Cancel Order"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
